import { Component, OnInit } from '@angular/core';
import { AppConst } from '@app/App.const';
import { CommonMethodsService } from '@app/shared/common/services/common-methods.service';
import {
  AuthorizedRegion,
  RegionHandoffRequest,
  RegionHandoffResponse,
} from '@app/shared/common/utils/app-interface';
import { getCurrentUser } from '@app/shared/common/utils/app-utils';
import { NavigationService } from '@app/shared/common/navigation/navigation.service';
import { SettingsService } from '@app/shared/common/services/Authentication/settings.service';

@Component({
  selector: 'app-region-selection',
  templateUrl: './region-selection.component.html',
  styleUrls: ['./region-selection.component.scss'],
  standalone: false,
})
export class RegionSelectionComponent implements OnInit {
  public regions: AuthorizedRegion[] = [];
  public selectedRegionName: string | null = null;
  public isLoading = true;
  public regionsLoaded = false;
  public isRedirecting = false;
  public errorMessage = '';

  constructor(
    private commonService: CommonMethodsService,
    private navigationService: NavigationService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    const userId = getCurrentUser()?.userId;
    if (!userId) {
      this.isLoading = false;
      this.errorMessage = 'We could not identify your account. Please sign in again.';
      return;
    }

    this.commonService.getRegionsForUser(userId).subscribe({
      next: (response: AuthorizedRegion[]) => {
        this.isLoading = false;
        if (!Array.isArray(response)) {
          this.errorMessage = 'We could not load your available regions. Please try again.';
          return;
        }
        this.regions = response;
        this.regionsLoaded = true;
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'We could not load your available regions. Please try again.';
      },
    });
  }

  public get shouldShowRegionList(): boolean {
    return this.regionsLoaded && !this.isRedirecting;
  }

  public get isAccessDenied(): boolean {
    return this.regionsLoaded && this.regions.length === 0;
  }

  public logout(): void {
    this.settingsService.logoff();
  }

  public selectRegion(region: AuthorizedRegion): void {
    if (this.isRedirecting) {
      return;
    }

    this.selectedRegionName = region.regionName;
    this.errorMessage = '';
  }

  public continueToSelectedRegion(): void {
    if (this.isRedirecting || !this.selectedRegionName) {
      return;
    }

    const region = this.regions.find(
      (availableRegion) =>
        availableRegion.regionName === this.selectedRegionName
    );
    if (!region) {
      return;
    }

    if (this.isGlobalRegion(region)) {
      this.continueOnGlobalRegion(region);
      return;
    }

    this.errorMessage = '';
    this.isRedirecting = true;

    const currentUser = getCurrentUser();
    if (!currentUser?.userId || !currentUser?.email) {
      this.showHandoffError();
      return;
    }

    this.commonService.getRegionHandoffCsrf().subscribe({
      next: (csrfResponse) => {
        if (!csrfResponse?.csrfToken) {
          this.showHandoffError();
          return;
        }

        const request: RegionHandoffRequest = {
          regionKey: region.regionName,
          userId: currentUser.userId,
          username: currentUser.email,
        };

        this.commonService
          .createRegionHandoffCode(request, csrfResponse.csrfToken)
          .subscribe({
            next: (response: RegionHandoffResponse) => {
              const redirectUrl = this.getRegionRedirectUrl(
                region.regionRedirectUrl,
                response?.handoffCode
              );
              if (!redirectUrl) {
                this.showHandoffError();
                return;
              }
              this.settingsService.clearLocalSession();
              this.redirectToRegion(redirectUrl);
            },
            error: () => this.showHandoffError(),
          });
      },
      error: () => this.showHandoffError(),
    });
  }

  private isGlobalRegion(region: AuthorizedRegion): boolean {
    if (!region.regionRedirectUrl) {
      return false;
    }

    try {
      return (
        new URL(region.regionRedirectUrl).origin === window.location.origin
      );
    } catch {
      return false;
    }
  }

  private continueOnGlobalRegion(region: AuthorizedRegion): void {
    this.selectedRegionName = region.regionName;
    this.errorMessage = '';
    this.isRedirecting = true;
    sessionStorage.setItem(AppConst.REGION_SELECTION_COMPLETED, 'true');

    this.navigationService.navigateTo(
      this.commonService.getPrivacyAccepted()
        ? this.navigationService.getHomePageUrl
        : this.navigationService.getPrivacyNoticeUrl
    );
  }

  public getRegionRedirectUrl(
    regionRedirectUrl: string,
    handoffCode: string | undefined
  ): string | null {
    if (!regionRedirectUrl || !handoffCode) {
      return null;
    }

    try {
      const redirectUrl = new URL(regionRedirectUrl);
      if (redirectUrl.protocol !== 'https:') {
        return null;
      }
      redirectUrl.searchParams.set('handoffCode', handoffCode);
      return redirectUrl.toString();
    } catch {
      return null;
    }
  }

  public redirectToRegion(url: string): void {
    window.location.assign(url);
  }

  private showHandoffError(): void {
    this.isRedirecting = false;
    this.errorMessage =
      'We could not connect you to that region. Please try again.';
  }
}
